import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../../base.component';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable, distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs';
import { Prompt } from '@models/prompt.model';
import { UntypedFormControl } from '@angular/forms';
import { UserConfigsService } from '@services/user-configs.service';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { currentPrompts } from '@store/ai-insights/prompts/prompts.selector';
import { CustomAiPromptComponent } from '@dialogs/custom-ai-prompt/custom-ai-prompt.component';
import { AiEngineModels, AiPilotAssistantStatus, AiPilotAssistantStore } from '@store/component/ai-pilot-assistant.store';

@Component({
  selector: 'lex-ai-pilot-prompt',
  templateUrl: './ai-pilot-prompt.component.html',
  styleUrls: ['./ai-pilot-prompt.component.scss'],
  standalone: false
})
export class AiPilotPromptComponent extends BaseComponent implements OnInit, OnDestroy {

  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @ViewChild('scrollResult') private scrollContainer: ElementRef;

  public status$: Observable<AiPilotAssistantStatus> = this.aiPilotAssistantStore.currentStatus$;
  public response$: Observable<string> = this.aiPilotAssistantStore.currentResponse$;
  public localPrompts: Prompt[];

  public isLoading: boolean = false;
  public isResultLoading: boolean = false;
  public showRetry: boolean = false;

  public showPromptControl: boolean = true;

  public selectedAiEngineModel: number = 4;
  public selectedPrompt: Prompt = null;
  public promptControl = new UntypedFormControl('');
  public hoverPromptId: string = '';
  public selectedSearchType: number = 0;

  public currentUserId = '';

  public mode: string = 'indeterminate';

  public aiEngines = AiEngineModels;

  private currentModel: string;
  private currentWorkflowKey: string;

  private globalPrompts$: Observable<Prompt[]> = this.store.select(currentPrompts());
  private globalPrompts: Prompt[];

  private shouldAutoScroll: boolean = true;

  constructor(
    public userConfigsService: UserConfigsService,
    private store: Store,
    private aiPilotAssistantStore: AiPilotAssistantStore,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AiPilotPromptComponent>) {
    super();

    this.currentUserId = this.userConfigsService?.currentConfig?.userId;

    this.globalPrompts$.pipe(
      filter((prompts) => !!prompts),
      map((prompts) => {
        this.aiPilotAssistantStore.addPrompts(prompts)
        return prompts
      }),
      tap((prompts) => this.globalPrompts = prompts),
      takeUntil(this.destroy$)
    ).subscribe();

    this.aiPilotAssistantStore.prompts$.pipe(
      map((prompts) => this.localPrompts = prompts),
      takeUntil(this.destroy$)
    ).subscribe();

    this.aiPilotAssistantStore.currentPrompt$.pipe(
      map((current) => this.selectedPrompt = current),
      tap((prompt) => {
        if (prompt) {
          this.promptControl.setValue(prompt.title);
          this.promptControl.updateValueAndValidity();
          this.showPromptControl = false;
        }
        else {
          this.promptControl.setValue('');
        }
        this.reloadPrompts();
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.aiPilotAssistantStore.currentWorkflowKey$.pipe(
      map((workflowKey) => {
        this.currentWorkflowKey = workflowKey;
        if (workflowKey)
          this.aiPilotAssistantStore.scheduleStreaming({ waitInMs: 5000, autoScroll: this.autoScroll.bind(this) });
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.status$.pipe(
      map((status) => {
        this.showRetry = false;
        switch (status) {
          case 'waiting':
            this.autoScroll();
            this.setLoadingStatus(false);
            break;
          case 'prompting':
            this.mode = 'query';
            this.setLoadingStatus(true);
            break;
          case 'responding':
            this.mode = 'indeterminate';
            this.shouldAutoScroll = true;
            this.setLoadingStatus(true);
            break;
          case 'error':
            this.showRetry = true;
            this.setLoadingStatus(false);
            break;
        }
      }),
      takeUntil(this.destroy$)).subscribe();

    this.promptControl.valueChanges.pipe(
      distinctUntilChanged(),
      map((data: string | any) => {
        this.reloadPrompts();
        if (data && typeof (data) === 'string') {
          if (!this.selectedPrompt || data !== this.selectedPrompt.title) {
            if (data.length < 256)
              this.filterLocalPrompts(data);
            else
              this.localPrompts = [];
          }
        }
        else if (data && typeof (data) !== 'string') {
          this.filterLocalPrompts(data.title);
          this.aiPilotAssistantStore.setPrompt(data);
        }
        else {
          this.reloadPrompts();
          this.aiPilotAssistantStore.setPrompt(null);
          this.showRetry = false;
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngOnInit() {
    this.aiPilotAssistantStore.initialize(this.selectedAiEngineModel);
  }

  override ngOnDestroy(): void {
    this.aiPilotAssistantStore.setPrompt(null);
    if (this.isLoading)
      this.cancelRequest();
    super.ngOnDestroy();
  }

  public onScroll(event): void {
    this.isResultLoading = true;
    if (Math.abs(event.srcElement.scrollHeight - event.srcElement.scrollTop - event.srcElement.clientHeight) < 2)
      this.shouldAutoScroll = true;
    else
      this.shouldAutoScroll = false;
  }

  public displayFn(prompt: Prompt): string {
    return prompt?.fullPrompt || '';
  }

  public enterPrompt(): void {
    const data = this.promptControl.value;
    if (typeof (data) === 'string') {
      if (this.promptControl.value.length === 0)
        return;
      this.aiPilotAssistantStore.createAndPrompt(this.promptControl.value);
      return;
    }
    this.showPromptControl = false;
    this.aiPilotAssistantStore.answerPrompt();
  }

  public togglePromptControl(): void {
    if (!this.isLoading) {
      this.showPromptControl = true;
      if (this.selectedPrompt) {
        this.promptControl.setValue(this.selectedPrompt);
      }
    }
  }

  public clearPrompt(): void {
    this.promptControl.setValue('');
    this.showPromptControl = true;
  }

  public cancelRequest(): void {
    this.aiPilotAssistantStore.cancelRequest();
  }

  public deletePrompt(promptId: string) {
    this.aiPilotAssistantStore.deletePrompt(promptId);
  }

  public promptSettings(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '900px';
    dialogConfig.maxHeight = '480px';
    dialogConfig.autoFocus = false;
    dialogConfig.data = this.selectedPrompt;
    if (this.isDarkMode())
      dialogConfig.panelClass = 'dark-theme';
    const dialogObservable = this.dialog.open(CustomAiPromptComponent, dialogConfig).afterClosed();

    dialogObservable.pipe(
      tap(() => {
        if (this.selectedPrompt) {
          this.aiPilotAssistantStore.refreshCurrentPrompt();
        }
        else {
          this.reloadPrompts();
        }
      })).subscribe();
  }

  public changeAiEngine(): void {
    this.aiPilotAssistantStore.setAiEngineModel(this.selectedAiEngineModel);
    this.promptControl.setValue('');
    this.resetAll();
  }

  public updateSearchType(): void {
    if (this.selectedSearchType === 0)
      this.selectedSearchType = 1;
    else
      this.selectedSearchType = 0;
    this.aiPilotAssistantStore.setSearchType(this.selectedSearchType);
  }

  public copyResponseToClipboard(): void {
    this.aiPilotAssistantStore.copyResponseToClipboard();
  }

  public selectPromptChange(event: MatAutocompleteSelectedEvent): void {
    this.showPromptControl = true;
    this.promptControl.setValue(event.option.value);
  }

  public close(): void {
    this.dialogRef.close();
  }

  public showClearButton(): boolean {
    const promptValue = this.promptControl.value;
    if (promptValue && typeof (promptValue) === 'string')
      return !this.isLoading && promptValue?.length > 0;
    if (promptValue)
      return !this.isLoading;
    return false;
  }

  private resetAll() {
    this.showRetry = false;
  }

  private reloadPrompts(): void {
    this.aiPilotAssistantStore.clearPrompts();
    this.aiPilotAssistantStore.addPrompts(this.globalPrompts);
  }

  private filterLocalPrompts(term: string = ''): void {
    this.localPrompts = this.localPrompts.filter(p =>
      p.title.toLocaleLowerCase().includes(term.toLocaleLowerCase())
      || p.fullPrompt.toLocaleLowerCase().includes(term.toLocaleLowerCase()));
  }

  private setLoadingStatus(isLoading: boolean) {
    if (isLoading) {
      this.isLoading = true;
      this.autocomplete?.closePanel();
      this.promptControl.disable();
    }
    else {
      this.isLoading = false;
      this.isResultLoading = false;
      this.promptControl.enable();
      if (this.selectedPrompt) {
        this.filterLocalPrompts(this.selectedPrompt.title);
      }
    }
  }

  private autoScroll(): void {
    if (this.shouldAutoScroll) {
      try {
        if (this.scrollContainer?.nativeElement) {
          setTimeout(() => this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight);
        }
        // eslint-disable-next-line
      } catch (err) { }
    }
  }
} 
